/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { ParticipantTermOfUse } from './Consents/ParticpantTermOfUse';
import { ParticipantElectronicSignatureConsents } from './Consents/ParticipantElectronicSignatureConsents';
import { ParticipantPrivacyConsents } from './Consents/ParticipantPrivacyConsents';
import { ContextualHelp } from './ContextualHelpList';
import { find } from '@utils/lodash';

export enum TextScreenID {
  WHAT_TYPES_OF_ID_CAN_I_USE = 'WHAT_TYPES_OF_ID_CAN_I_USE',
  MULTIPLE_SIGNERS = 'MULTIPLE_SIGNERS',
  TERMS_OF_USE = 'TERMS_OF_USE',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  CONSENT_TO_USE_ELECTRONIC_SIGNATURES_AND_RECORDS = 'CONSENT_TO_USE_ELECTRONIC_SIGNATURES_AND_RECORDS',
  SIGNING_ROOM_INSTRUCTION = 'SIGNING_ROOM_INSTRUCTION',
  SIGNING_ROOM_CLOSED_CAPTION_CONTEXTUAL_HELP = 'SIGNING_ROOM_CLOSED_CAPTION_CONTEXTUAL_HELP',
  DEVICE_SETUP = 'DEVICE_SETUP',
}

export const textScreens = [
  {
    textScreenId: 'WHAT_TYPES_OF_ID_CAN_I_USE',
    linkText: 'What types of ID can I use?',
    title: 'Additional Help',
    htmlText:
      '<h2>What types of ID can I use?</h2><p>For ID verification, you can use a driver’s license or passport.</p>',
  },
  {
    textScreenId: 'MULTIPLE_SIGNERS',
    linkText: 'How does an online notarization work with multiple participants?',
    title: 'Multiple participants in an online notarization',
    htmlText: `
    <h2>Multiple participants in an online notarization</h2>
    <p>
      Blend Signing Room allows for multiple participants across any number of devices.
    </p>
    <p>
      You decide who will be using this device to validate their identity and sign documents. We recommend only selecting those who are in 
      the same location as you.
    </p>`,
  },
  {
    textScreenId: 'TERMS_OF_USE',
    linkText: 'Terms of Use',
    title: 'Terms of Use',
    dialogHeader: 'Terms of Use',
    htmlText: ParticipantTermOfUse,
  },
  {
    textScreenId: 'PRIVACY_POLICY',
    linkText: 'Privacy Policy',
    title: 'Privacy Policy',
    dialogHeader: 'Privacy Policy',
    htmlText: ParticipantPrivacyConsents,
  },
  {
    textScreenId: 'CONSENT_TO_USE_ELECTRONIC_SIGNATURES_AND_RECORDS',
    linkText: 'Consent to Use Electronic Signatures and Records',
    title: 'Consent to Use Electronic Signatures and Records',
    dialogHeader: 'Consent to Use Electronic Signatures and Records',
    htmlText: ParticipantElectronicSignatureConsents,
  },
  {
    textScreenId: 'SIGNING_ROOM_CLOSED_CAPTION_CONTEXTUAL_HELP',
    linkText: 'What if I need to enable closed captions for accessibility?',
    title: 'What if I need to enable closed captions for accessibility?',
    htmlText: `<h2>What if I need to enable closed captions for accessibility?</h2>
    <p>
      Once you enter Blend Signing Room, enable closed captions by going to <strong>Settings > Enable Closed Captions</strong>
    </p>
    <p>
      Blend Signing Room allows our customers to connect with notaries via video call and participate in online closings.
    </p>
    `,
  },
  {
    textScreenId: 'SIGNING_ROOM_INSTRUCTION',
    linkText: 'Review Blend Signing Room instructions',
    title: 'Blend Signing Room instructions',
    htmlText: `<h2>Blend Signing Room instructions</h2>
    <p>We're partnering with Blend Signing Room to help you connect with a notary via video call and participate in an online notarization. A Blend Signing Room notarization allows for multiple users across any number of devices. You can decide who will be using the same device to validate their identity and sign documents.</p> 
    <p>
      When it’s time for your appointment, you will need the following items:
    </p>
    <ul>
      <li>a photo ID (either a driver’s license or passport</li>
      <li>a smartphone with a camera</li>
      <li>a computer (<em>optional, but recommended</em>)</li>
    </ul>
    <p>Please note the following requirements for Blend Signing Room:</p>
    <ul>
      <li>Blend Signing Room is <strong>not</strong> compatible with Internet Explorer</li>
    </ul>`,
  },
  {
    textScreenId: 'DEVICE_SETUP',
    linkText: 'How can I use my video camera and microphone?',
    title: 'Use your video camera and microphone',
    htmlText: `
      <h2>Use your video camera and microphone</h2>
      <p>
        You may need to change your browser’s permissions
        to allow Blend Signing Room to use your video camera
        and microphone for this appointment. To learn how,
        find your browser below and click on the link for instructions.
      </p>
      <p>
        Supported browsers:
      </p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop#zippy=%2Cturn-on-permissions-in-computer-settings" target="_blank">Chrome</a></li>
        <li><a href="https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac" target="_blank">Safari</a></li>
        <li><a href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions" target="_blank">Firefox</a></li>
        <li><a href="https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857#ID0EBD=Windows_10" target="_blank">Microsoft Edge</a></li>
      </ul>
`,
  },
];

export const getTextScreenDefinition = (textScreenId: string) => {
  const textScreen = find(textScreens, { textScreenId });
  if (textScreen) {
    return textScreen;
  } else {
    throw new Error(`Text screen definition not found for textScreenId: ${textScreenId}`);
  }
};

export const contextualHelpRoomInstructions: ContextualHelp = {
  linkText: 'Review Blend Signing Room instructions',
  textScreenId: 'SIGNING_ROOM_INSTRUCTION',
};

export const contextualHelpClosedCaptionSupported: ContextualHelp = {
  linkText: 'What if I need to enable closed captions for accessibility?',
  textScreenId: 'SIGNING_ROOM_CLOSED_CAPTION_CONTEXTUAL_HELP',
};
